/**
 * Dashboard Store ratings card (#2379): each app's score as its store shows it, then, under "By country and star",
 * the App Store country by country and both stores' split by star. syncStoreRatingsDaily writes the numbers to
 * `globals/store_ratings` (functions/store_ratings_sync.js); this file reads them, draws them, and asks
 * syncStoreRatingsManual for fresh ones on Refresh. Google Play's public rating is worldwide, so it has no countries.
 */

const STORES = [
  { key: "app_store", label: "App Store", platform: "iOS" },
  { key: "google_play", label: "Google Play", platform: "Android" }
];
const STAR_LEVELS = [5, 4, 3, 2, 1];

// Tailwind class lists; whole strings, so Tailwind finds them.
const EMPTY = "px-4 py-8 text-center text-sm text-gray-500 sm:px-6 dark:text-gray-400";
const NUM = "py-2 pl-3 text-right tabular-nums";
const HEAD = "py-2 pl-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400";

const panel = {
  deps: null,
  status: "idle", // idle | loading | refreshing | ok | error
  data: null,
  error: null
};

let refreshBound = false;

/** Reads `globals/store_ratings` and draws it. `deps`: `{ db, doc, getDoc, functions, httpsCallable, showToast }`. */
export async function loadStoreRatings(deps = panel.deps) {
  if (!deps?.db || !deps?.getDoc) return;
  panel.deps = deps;
  bindRefresh();
  if (panel.status === "loading" || panel.status === "refreshing") return;
  panel.status = "loading";
  panel.error = null;
  renderStoreRatingsPanel();
  await readRatings();
  renderStoreRatingsPanel();
}

async function readRatings() {
  const { db, doc, getDoc } = panel.deps;
  try {
    const snap = await getDoc(doc(db, "globals", "store_ratings"));
    panel.data = snap.exists() ? snap.data() : null;
    panel.status = "ok";
  } catch (err) {
    console.error("❌ Reading globals/store_ratings failed:", err);
    panel.error = err?.message || String(err);
    panel.status = "error";
  }
}

/** Asks the stores again (syncStoreRatingsManual, about ten seconds), then reads the new numbers. */
async function refreshStoreRatings() {
  const deps = panel.deps;
  if (!deps?.functions || !deps?.httpsCallable || panel.status === "refreshing" || panel.status === "loading") return;
  panel.status = "refreshing";
  renderStoreRatingsPanel();
  try {
    const result = await deps.httpsCallable(deps.functions, "syncStoreRatingsManual")({});
    if (result?.data?.success === false) deps.showToast?.("One store couldn't be read; its last good numbers are shown.", "warning");
  } catch (err) {
    console.error("❌ syncStoreRatingsManual failed:", err);
    deps.showToast?.(`Couldn't refresh the store ratings: ${err?.message || err}`, "error");
  }
  await readRatings();
  renderStoreRatingsPanel();
}

/** Draws the current state into the dashboard template, if it is mounted. */
export function renderStoreRatingsPanel() {
  const body = document.getElementById("dashRatings");
  const detail = document.getElementById("dashRatingsDetail");
  const meta = document.getElementById("dashRatingsMeta");
  const button = document.getElementById("dashRatingsRefresh");
  if (!body) return;
  if (button) button.disabled = panel.status === "loading" || panel.status === "refreshing" || !panel.deps?.functions;
  if (meta) meta.textContent = metaText(panel);

  if (panel.status === "error") {
    body.innerHTML = `<p class="px-4 py-8 text-center text-sm text-red-700 sm:px-6 dark:text-red-400">Could not read the store ratings: ${escapeHtml(panel.error)}</p>`;
  } else if (panel.data) {
    body.innerHTML = ratingsSummaryHtml(panel.data);
  } else {
    body.innerHTML = `<p class="${EMPTY}">${panel.status === "ok" ? "Not checked yet: the first daily check runs at 05:30 UTC, or press Refresh." : "Loading store ratings…"}</p>`;
  }
  if (detail) {
    detail.innerHTML = panel.data && panel.status !== "error"
      ? ratingsDetailHtml(panel.data)
      : `<p class="${EMPTY}">${panel.status === "error" ? "Nothing to show until the ratings can be read." : "Nothing checked yet."}</p>`;
  }
}

function bindRefresh() {
  if (refreshBound) return;
  refreshBound = true;
  document.addEventListener("click", (e) => {
    if (e.target.closest("#dashRatingsRefresh")) refreshStoreRatings();
  });
}

function metaText({ status, data }) {
  if (status === "refreshing") return "Asking the App Store and Google Play…";
  if (!data?.updated_at) return "As each store shows it, checked daily.";
  return `As each store shows it, checked daily; last checked ${formatDateTime(data.updated_at)}.`;
}

function formatDateTime(iso) {
  const date = new Date(iso);
  if (!iso || Number.isNaN(date.getTime())) return "at an unknown time";
  return `${date.toLocaleDateString([], { month: "short", day: "numeric" })}, ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

/** "4.9" or "–". */
export function formatAverage(average) {
  return typeof average === "number" ? average.toFixed(1) : "–";
}

/** Five stars, filled to the nearest half of `average`, for the eye; the number beside them is what counts. */
function starsHtml(average, size = "size-5") {
  const filled = typeof average === "number" ? Math.round(average * 2) / 2 : 0;
  return `<span class="flex items-center" aria-hidden="true">${[1, 2, 3, 4, 5].map((i) => {
    const tone = i <= filled ? "text-amber-400" : i - 0.5 === filled ? "text-amber-300" : "text-gray-200 dark:text-white/15";
    return `<svg viewBox="0 0 20 20" fill="currentColor" class="${size} ${tone}"><path d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401Z" clip-rule="evenodd" fill-rule="evenodd" /></svg>`;
  }).join("")}</span>`;
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

function scopeText(section) {
  const total = section?.total || {};
  if (section?.scope === "worldwide") return "worldwide";
  const n = total.country_count || 0;
  return n ? `in ${plural(n, "country", "countries")}` : "in no country yet";
}

function staleHtml(section) {
  if (!section?.error) return "";
  const since = section.synced_at ? `numbers from ${formatDateTime(section.synced_at)}` : "no numbers yet";
  return `<p class="mt-2 text-xs text-amber-700 dark:text-amber-400">Last check failed (${escapeHtml(section.error)}); showing ${escapeHtml(since)}.</p>`;
}

/** The score of each store, always shown. */
export function ratingsSummaryHtml(data) {
  return `
    <dl class="grid grid-cols-1 gap-x-6 gap-y-5 px-4 py-5 sm:grid-cols-2 sm:px-6">${STORES.map((store) => {
      const section = data?.[store.key] || {};
      const total = section.total || {};
      const count = total.count || 0;
      return `
      <div>
        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">${store.label} <span class="font-normal">· ${store.platform}</span></dt>
        <dd class="mt-1 flex items-center gap-x-3">
          <span class="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">${count ? formatAverage(total.average) : "–"}</span>
          ${count ? starsHtml(total.average) : ""}
        </dd>
        <dd class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">${escapeHtml(`${plural(count, "rating", "ratings")} ${scopeText(section)}`)}</dd>
        ${staleHtml(section)}
      </div>`;
    }).join("")}
    </dl>`;
}

/** Bars for a `[5★…1★]` split. */
function starBarsHtml(stars) {
  if (!Array.isArray(stars)) return `<p class="text-sm text-gray-500 dark:text-gray-400">No split by star.</p>`;
  const most = Math.max(1, ...stars);
  return `<div class="flex flex-col gap-1.5">${STAR_LEVELS.map((level, i) => `
      <div class="flex items-center gap-3 text-sm">
        <span class="w-6 text-right text-gray-500 tabular-nums dark:text-gray-400">${level}★</span>
        <span class="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-white/10"><span class="block h-full rounded-full bg-amber-400" style="width: ${Math.round((stars[i] / most) * 100)}%"></span></span>
        <span class="w-10 text-right text-gray-900 tabular-nums dark:text-white">${stars[i]}</span>
      </div>`).join("")}
    </div>`;
}

function countryTableHtml(countries) {
  if (!countries.length) return `<p class="text-sm text-gray-500 dark:text-gray-400">No country has a rating yet.</p>`;
  const rows = countries.map((c) => `
      <tr>
        <th scope="row" class="py-2 text-left font-medium text-gray-900 dark:text-white"><span aria-hidden="true" class="mr-1.5">${escapeHtml(c.flag || "")}</span>${escapeHtml(c.name || c.code)}</th>
        <td class="${NUM} font-semibold text-gray-900 dark:text-white">${formatAverage(c.average)}</td>
        <td class="${NUM} text-gray-700 dark:text-gray-300">${c.count}</td>
        ${STAR_LEVELS.map((level, i) => `<td class="${NUM} text-gray-500 dark:text-gray-400">${Array.isArray(c.stars) ? c.stars[i] : "–"}</td>`).join("")}
      </tr>`).join("");
  return `
    <div class="relative overflow-x-auto">
      <table class="w-full text-sm">
        <thead class="border-b border-gray-200 dark:border-white/10"><tr>
          <th scope="col" class="py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">Country</th>
          <th scope="col" class="${HEAD}">Average</th>
          <th scope="col" class="${HEAD}">Ratings</th>
          ${STAR_LEVELS.map((level) => `<th scope="col" class="${HEAD}">${level}★</th>`).join("")}
        </tr></thead>
        <tbody class="divide-y divide-gray-100 dark:divide-white/5">${rows}</tbody>
      </table>
    </div>`;
}

/** Behind "By country and star": each store's split by star side by side, then the countries at full width. */
export function ratingsDetailHtml(data) {
  const byStar = STORES.map((store) => {
    const section = data?.[store.key] || {};
    const note = section.scope === "worldwide"
      ? `<p class="text-sm text-gray-500 dark:text-gray-400">Google Play shows one worldwide rating, not one per country.</p>`
      : "";
    return `
      <section class="flex flex-col gap-4">
        <h4 class="text-sm font-semibold text-gray-900 dark:text-white">${store.label}</h4>
        ${starBarsHtml(section.total?.stars)}
        ${note}
      </section>`;
  }).join("");
  const byCountry = STORES.filter((store) => data?.[store.key]?.scope !== "worldwide").map((store) => {
    const countries = Array.isArray(data?.[store.key]?.countries) ? data[store.key].countries : [];
    return `
      <section class="flex flex-col gap-2">
        <h4 class="text-sm font-semibold text-gray-900 dark:text-white">${store.label} by country</h4>
        ${countryTableHtml(countries)}
      </section>`;
  }).join("");
  return `
    <div class="grid grid-cols-1 gap-x-10 gap-y-8 px-4 py-5 sm:px-6 lg:grid-cols-2">${byStar}
    </div>
    <div class="flex flex-col gap-8 border-t border-gray-200 px-4 py-5 sm:px-6 dark:border-white/10">${byCountry}
    </div>`;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
